import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash, faCog, faTimes, faLink, faCalculator,
  faChartLine, faChartBar, faTable, faDollarSign,
  faUsers, faCloud, faExchangeAlt, faCode
} from '@fortawesome/free-solid-svg-icons';
import { MetricComponent } from '../types/templateDesigner';

interface Connection {
  id: string;
  from: string;
  to: string;
  formula?: string;
  label?: string;
}

// Helper function to create elbow connector path
const createElbowPath = (x1: number, y1: number, x2: number, y2: number): string => {
  const midX = (x1 + x2) / 2;
  // Create orthogonal (elbow) path
  return `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
};

// Derive a short, human-readable label from a formula like "={A}/{B}" → "A per B"
const autoDetectLabel = (formula: string): string | null => {
  const f = formula.replace(/^=/, '').trim();
  const patterns: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
    [/^\{([^}]+)\}\s*\/\s*\{([^}]+)\}$/, m => `${m[1]} per ${m[2]}`],
    [/^\{([^}]+)\}\s*\*\s*\{([^}]+)\}$/, m => `${m[1]} × ${m[2]}`],
    [/^\{([^}]+)\}\s*\+\s*\{([^}]+)\}$/, m => `${m[1]} + ${m[2]}`],
    [/^\{([^}]+)\}\s*-\s*\{([^}]+)\}$/, m => `${m[1]} − ${m[2]}`],
    [/^\(?\{([^}]+)\}\s*-\s*\{([^}]+)\}\)?\s*\/\s*\{([^}]+)\}\s*\*\s*100$/, m => `${m[1]} margin %`],
    [/^\{([^}]+)\}\s*\/\s*\{([^}]+)\}\s*\*\s*100$/, m => `${m[1]} as % of ${m[2]}`],
  ];
  for (const [re, build] of patterns) {
    const match = f.match(re);
    if (match) return build(match);
  }
  return null;
};

// ============================================================================
// CANVAS
// ============================================================================

interface CanvasProps {
  components: MetricComponent[];
  connections: Connection[];
  selectedComponent: MetricComponent | null;
  connectingFrom: string | null;
  executionResult: any;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onComponentClick: (component: MetricComponent) => void;
  onComponentDelete: (id: string) => void;
  onComponentUpdate: (id: string, updates: Partial<MetricComponent>) => void;
  onStartConnection: (id: string) => void;
  onCompleteConnection: (id: string) => void;
}

export const Canvas = forwardRef<HTMLDivElement, CanvasProps>(({
  components,
  connections,
  selectedComponent,
  connectingFrom,
  executionResult,
  onDrop,
  onDragOver,
  onComponentClick,
  onComponentDelete,
  onComponentUpdate,
  onStartConnection,
  onCompleteConnection
}, ref) => {
  // Track connection dragging state. Use REFS for source/active flags so the
  // document-level mouseup handler (attached once on mount) always sees the
  // freshest values without re-attach races between mousedown and mouseup.
  const [isDraggingConnection, setIsDraggingConnection] = useState(false);
  const [dragConnectionPos, setDragConnectionPos] = useState({ x: 0, y: 0 });
  const [expandedConnId, setExpandedConnId] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const dragFromRef = useRef<string | null>(null);
  const isDraggingRef = useRef(false);
  const onStartConnectionRef = useRef(onStartConnection);
  const onCompleteConnectionRef = useRef(onCompleteConnection);

  // Keep callback refs current so the once-mounted listener uses fresh callbacks
  useEffect(() => { onStartConnectionRef.current = onStartConnection; }, [onStartConnection]);
  useEffect(() => { onCompleteConnectionRef.current = onCompleteConnection; }, [onCompleteConnection]);

  // Start a connection drag from an output port
  const handleConnectionDragStart = (componentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    dragFromRef.current = componentId;
    isDraggingRef.current = true;
    setIsDraggingConnection(true);

    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setDragConnectionPos({
        x: e.clientX - rect.left + canvasRef.current.scrollLeft,
        y: e.clientY - rect.top + canvasRef.current.scrollTop
      });
    }
  };

  // Mount once: attach document-level mousemove/mouseup. Refs ensure the
  // handler always reads the latest drag source even if state hasn't flushed.
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      setDragConnectionPos({
        x: e.clientX - rect.left + canvasRef.current.scrollLeft,
        y: e.clientY - rect.top + canvasRef.current.scrollTop
      });
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const sourceId = dragFromRef.current;

      const element = document.elementFromPoint(e.clientX, e.clientY);
      const targetEl = element?.closest('[data-component-id]');
      const targetComponentId = targetEl?.getAttribute('data-component-id');

      console.log('🔵 Drop detected:', {
        sourceId,
        targetId: targetComponentId,
        elementUnderCursor: element?.tagName,
        targetFound: !!targetEl
      });

      if (targetComponentId && sourceId && targetComponentId !== sourceId) {
        console.log('✅ Connecting', sourceId, '→', targetComponentId);
        onStartConnectionRef.current(sourceId);
        setTimeout(() => onCompleteConnectionRef.current(targetComponentId), 10);
      } else {
        console.log('❌ No connection —',
          !targetComponentId ? 'no target component under cursor' :
          !sourceId ? 'no source' :
          'dropped on same source');
      }

      dragFromRef.current = null;
      isDraggingRef.current = false;
      setIsDraggingConnection(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div
      ref={(node) => {
        canvasRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      onDrop={onDrop}
      onDragOver={onDragOver}
      className="flex-1 bg-gray-50 relative overflow-auto"
      style={{
        backgroundImage: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }}
    >
      {/* SVG Layer for Connections */}
      <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 5, width: '100%', height: '100%' }}>
        <defs>
          {/* Arrow marker definition */}
          <marker
            id="arrowhead"
            markerWidth="12"
            markerHeight="12"
            refX="10"
            refY="4"
            orient="auto"
          >
            <polygon points="0 0, 12 4, 0 8" fill="#7c3aed" />
          </marker>

          {/* Glow filter for connections */}
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Existing saved connections */}
        {connections.map((conn) => {
          const fromComp = components.find(c => c.id === conn.from);
          const toComp = components.find(c => c.id === conn.to);

          if (!fromComp || !toComp) return null;

          // Calculate connection points (right edge of source, left edge of target)
          const x1 = fromComp.position.x + 192; // Right edge of component
          const y1 = fromComp.position.y + 40; // Middle height
          const x2 = toComp.position.x; // Left edge of component
          const y2 = toComp.position.y + 40; // Middle height

          // Create elbow path
          const pathData = createElbowPath(x1, y1, x2, y2);

          // Calculate midpoint for formula label
          const midX = (x1 + x2) / 2;
          const midY = (y1 + y2) / 2;

          return (
            <g key={conn.id}>
              {/* Shadow/glow path */}
              <path
                d={pathData}
                fill="none"
                stroke="#c4b5fd"
                strokeWidth="6"
                opacity="0.3"
                filter="url(#glow)"
              />

              {/* Main connection path */}
              <path
                d={pathData}
                fill="none"
                stroke="#7c3aed"
                strokeWidth="2.5"
                markerEnd="url(#arrowhead)"
              />

              {/* Connection points */}
              <circle cx={x1} cy={y1} r="6" fill="#7c3aed" stroke="#fff" strokeWidth="2" />
              <circle cx={x2} cy={y2} r="6" fill="#7c3aed" stroke="#fff" strokeWidth="2" />

              {/* Formula label - clickable to open details popover */}
              {conn.formula && (() => {
                const fullFormula = conn.formula;
                // Priority: user-typed label → auto-detected ("Revenue per Customer Count") → raw formula
                const rawDisplay = conn.label?.trim() || autoDetectLabel(fullFormula) || fullFormula;
                const displayText = rawDisplay.length > 28 ? rawDisplay.slice(0, 27) + '…' : rawDisplay;
                const labelWidth = Math.max(60, displayText.length * 7 + 16);
                return (
                  <g
                    style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedConnId(prev => prev === conn.id ? null : conn.id);
                    }}
                  >
                    <title>{conn.label ? `${conn.label}: ${fullFormula}` : fullFormula} — click for details</title>
                    {/* Larger transparent hit area so the label is easy to click */}
                    <rect
                      x={midX - labelWidth / 2 - 4}
                      y={midY - 16}
                      width={labelWidth + 8}
                      height={32}
                      rx="6"
                      fill="transparent"
                    />
                    {/* Label background */}
                    <rect
                      x={midX - labelWidth / 2}
                      y={midY - 12}
                      width={labelWidth}
                      height="24"
                      rx="4"
                      fill="#7c3aed"
                      opacity="0.95"
                    />
                    {/* Formula text */}
                    <text
                      x={midX}
                      y={midY + 5}
                      textAnchor="middle"
                      fill="white"
                      fontSize="11"
                      fontWeight="600"
                      fontFamily="monospace"
                      style={{ pointerEvents: 'none' }}
                    >
                      {displayText}
                    </text>
                  </g>
                );
              })()}
            </g>
          );
        })}

        {/* Dragging connection preview */}
        {isDraggingConnection && dragFromRef.current && (() => {
          const fromComp = components.find(c => c.id === dragFromRef.current);
          if (!fromComp) return null;

          const x1 = fromComp.position.x + 192;
          const y1 = fromComp.position.y + 40;
          const x2 = dragConnectionPos.x;
          const y2 = dragConnectionPos.y;

          const pathData = createElbowPath(x1, y1, x2, y2);

          return (
            <g>
              <path
                d={pathData}
                fill="none"
                stroke="#3b82f6"
                strokeWidth="2.5"
                strokeDasharray="5,5"
                opacity="0.8"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="10"
                  dur="0.5s"
                  repeatCount="indefinite"
                />
              </path>
              <circle cx={x1} cy={y1} r="5" fill="#3b82f6" stroke="#fff" strokeWidth="2" />
              <circle cx={x2} cy={y2} r="5" fill="#3b82f6" stroke="#fff" strokeWidth="2" />
            </g>
          );
        })()}
      </svg>

      {/* Empty State */}
      {components.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">🎨</div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">
              Start Building Your Dynamic Dashboard
            </h3>
            <p className="text-xs text-gray-600 mb-4">
              Drag any metric from the left panel onto this canvas. Connect components by dragging
              from the blue arrow (→) on one component onto another component.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-left">
              <p className="text-xs font-semibold text-blue-900 mb-1">💡 Quick Tips:</p>
              <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                <li>Search for specific metrics in the library</li>
                <li>Drag the blue arrow (→) onto another component to connect</li>
                <li>Use Excel-style formulas with autocomplete</li>
                <li>Click "Generate Viz" to auto-create charts</li>
              </ul>
            </div>
          </div>
        </div>
      ) : (
        // Components
        components.map((component) => (
          <CanvasComponent
            key={component.id}
            component={component}
            isSelected={selectedComponent?.id === component.id}
            isConnecting={connectingFrom === component.id}
            connectionInProgress={connectingFrom !== null}
            isDraggingConnection={isDraggingConnection}
            executionResult={executionResult?.results?.[component.id]}
            onClick={() => onComponentClick(component)}
            onDelete={() => onComponentDelete(component.id)}
            onUpdate={(updates) => onComponentUpdate(component.id, updates)}
            onConnectionDragStart={handleConnectionDragStart}
            onStartConnection={onStartConnection}
            onCompleteConnection={onCompleteConnection}
          />
        ))
      )}

      {/* Connection details popover - opens when a formula label is clicked */}
      {expandedConnId && (() => {
        const conn = connections.find(c => c.id === expandedConnId);
        if (!conn || !conn.formula) return null;
        const fromComp = components.find(c => c.id === conn.from);
        const toComp = components.find(c => c.id === conn.to);
        if (!fromComp || !toComp) return null;

        const popX = (fromComp.position.x + 192 + toComp.position.x) / 2;
        const popY = (fromComp.position.y + toComp.position.y) / 2 + 60;

        // Resolve variable values referenced in {Name} placeholders
        const referencedVars = Array.from(conn.formula.matchAll(/\{([^}]+)\}/g)).map(m => m[1]);
        const resolvedVars = referencedVars.map(name => {
          const comp = components.find(c => c.name === name);
          const result = comp ? executionResult?.results?.[comp.id] : null;
          return {
            name,
            value: result?.formatted || (comp ? '(needs Execute)' : '(not found)')
          };
        });
        // Connection result lives on the connection itself, not on the target component.
        const result = executionResult?.connectionResults?.[conn.id];

        return (
          <div
            className="absolute z-40 bg-white rounded-lg shadow-2xl border-2 border-purple-300 p-4 w-72"
            style={{ left: popX - 144, top: popY }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-sm font-bold text-purple-900">
                  {conn.label?.trim() || autoDetectLabel(conn.formula) || 'Connection Details'}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  <span className="font-medium">{fromComp.name}</span>
                  <span className="mx-1">→</span>
                  <span className="font-medium">{toComp.name}</span>
                </div>
              </div>
              <button
                onClick={() => setExpandedConnId(null)}
                className="text-gray-400 hover:text-gray-700"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            </div>

            <div className="mb-3">
              <div className="text-xs text-purple-600 font-medium mb-1">Formula</div>
              <div className="text-xs text-purple-800 font-mono bg-purple-50 border border-purple-200 rounded p-2 break-all">
                {conn.formula}
              </div>
            </div>

            {resolvedVars.length > 0 && (
              <div className="mb-3">
                <div className="text-xs text-gray-600 font-medium mb-1">Resolved values</div>
                <div className="space-y-1">
                  {resolvedVars.map(v => (
                    <div key={v.name} className="text-xs flex justify-between bg-gray-50 border border-gray-200 rounded px-2 py-1">
                      <span className="font-mono text-gray-700">{`{${v.name}}`}</span>
                      <span className="font-semibold text-gray-900">{v.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-xs text-green-600 font-medium mb-1">Result</div>
              <div className={`text-sm font-bold rounded p-2 border ${
                result ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'
              }`}>
                {result?.formatted ?? 'Click "Execute" to compute'}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
});

// ============================================================================
// CANVAS COMPONENT
// ============================================================================

interface CanvasComponentProps {
  component: MetricComponent;
  isSelected: boolean;
  isConnecting: boolean;
  connectionInProgress: boolean;
  isDraggingConnection: boolean;
  executionResult?: any;
  onClick: () => void;
  onDelete: () => void;
  onUpdate: (updates: Partial<MetricComponent>) => void;
  onConnectionDragStart: (componentId: string, e: React.MouseEvent) => void;
  onStartConnection: (componentId: string) => void;
  onCompleteConnection: (componentId: string) => void;
}

const CanvasComponent: React.FC<CanvasComponentProps> = ({
  component,
  isSelected,
  isConnecting,
  connectionInProgress,
  isDraggingConnection,
  executionResult,
  onClick,
  onDelete,
  onUpdate,
  onConnectionDragStart,
  onStartConnection,
  onCompleteConnection
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isHoveringInput, setIsHoveringInput] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Don't drag component if clicking on ports or action buttons
    if ((e.target as HTMLElement).closest('.connection-port')) return;
    if ((e.target as HTMLElement).closest('.component-action')) return;

    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const canvas = document.querySelector('.relative.overflow-auto');
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left - dragOffset.x + (canvas as HTMLElement).scrollLeft;
    const y = e.clientY - rect.top - dragOffset.y + (canvas as HTMLElement).scrollTop;

    onUpdate({ position: { x: Math.max(0, x), y: Math.max(0, y) } });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  const getComponentIcon = () => {
    if (component.type === 'cost_source') return faCloud;
    if (component.type === 'calculated_metric') return faCalculator;
    if (component.config.dataType === 'currency') return faDollarSign;
    return faChartLine;
  };

  const getComponentColor = () => {
    if (component.type === 'cost_source') return 'orange';
    if (component.type === 'calculated_metric') return 'purple';
    if (component.config.dataType === 'currency') return 'green';
    return 'blue';
  };

  const colorClass = getComponentColor();

  return (
    <div
      data-component-id={component.id}
      className={`absolute w-48 bg-white border-2 rounded-lg shadow-lg cursor-move transition-all z-10 ${
        isSelected ? 'border-purple-500 ring-2 ring-purple-200 scale-105' : 'border-gray-300'
      } ${isConnecting ? 'ring-2 ring-blue-400 animate-pulse' : ''}`}
      style={{
        left: component.position.x,
        top: component.position.y
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        if (connectionInProgress && !isConnecting) {
          onCompleteConnection(component.id);
        } else {
          onClick();
        }
      }}
    >
      {/* Input Port (Left side) - Invisible but functional */}
      <div
        className="input-port connection-port absolute -left-3 top-1/2 -translate-y-1/2 w-7 h-7 z-30"
        data-component-id={component.id}
        onMouseEnter={() => setIsHoveringInput(true)}
        onMouseLeave={() => setIsHoveringInput(false)}
        style={{ opacity: 0 }}
      />

      {/* Output Port (Right side) - Invisible but functional for drag-to-connect */}
      <div
        className="output-port connection-port absolute -right-3 top-1/2 -translate-y-1/2 w-7 h-7 z-30"
        onMouseDown={(e) => {
          e.stopPropagation();
          onConnectionDragStart(component.id, e);
        }}
        onClick={(e) => {
          e.stopPropagation();
          onStartConnection(component.id);
        }}
        style={{ opacity: 0, cursor: 'crosshair' }}
      />

      {/* Header */}
      <div className={`px-3 py-2 bg-${colorClass}-50 border-b border-${colorClass}-200 rounded-t-lg`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <FontAwesomeIcon icon={getComponentIcon()} className={`text-${colorClass}-600`} />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-900 truncate">{component.name}</div>
              <div className="text-xs text-gray-500">{component.config.unit || component.config.dataType}</div>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="component-action text-gray-400 hover:text-red-600 transition-colors"
          >
            <FontAwesomeIcon icon={faTrash} className="text-xs" />
          </button>
        </div>
      </div>

      {/* Drag hint shown only while dragging from the blue arrow */}
      {isDraggingConnection && (
        <div className="px-3 py-1 text-xs text-blue-600 font-medium text-center animate-pulse">
          Drag to a component →
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PROPERTIES PANEL (unchanged from previous version)
// ============================================================================

interface PropertiesPanelProps {
  component: MetricComponent;
  onUpdate: (updates: Partial<MetricComponent>) => void;
  onClose: () => void;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({ component, onUpdate, onClose }) => {
  const [formula, setFormula] = useState(
    typeof component.config.formula === 'string' ? component.config.formula : ''
  );

  return (
    <div className="w-80 bg-white border-l border-gray-300 overflow-y-auto">
      <div className="p-3 border-b border-gray-300 flex items-center justify-between sticky top-0 bg-white">
        <h3 className="text-xs font-semibold text-gray-900 flex items-center">
          <FontAwesomeIcon icon={faCog} className="mr-2 text-purple-600" />
          Component Properties
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="p-3 space-y-4">
        {/* Name */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Display Name</label>
          <input
            type="text"
            value={component.name}
            onChange={(e) => onUpdate({ name: e.target.value, label: e.target.value })}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          />
        </div>

        {/* Type */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Component Type</label>
          <select
            value={component.type}
            onChange={(e) => onUpdate({ type: e.target.value as any })}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          >
            <option value="input_metric">Input Metric</option>
            <option value="cost_source">Cost Source</option>
            <option value="calculated_metric">Calculated Metric</option>
            <option value="kpi_display">KPI Display</option>
            <option value="chart_display">Chart Display</option>
          </select>
        </div>

        {/* Formula (for calculated metrics) */}
        {component.type === 'calculated_metric' && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Excel Formula
            </label>
            <textarea
              value={formula}
              onChange={(e) => setFormula(e.target.value)}
              onBlur={() => onUpdate({ config: { ...component.config, formula } })}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded font-mono"
              rows={4}
              placeholder="={ComponentA} / {ComponentB}"
            />
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
              <p className="text-xs font-semibold text-blue-900 mb-1">Formula Examples:</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Division: <code>={'{TotalCost}'} / {'{Customers}'}</code></li>
                <li>• Multiplication: <code>={'{Price}'} * {'{Quantity}'}</code></li>
                <li>• Sum: <code>=SUM({'{Cost1}'}, {'{Cost2}'})</code></li>
                <li>• Percentage: <code>=({'{Revenue}'} - {'{Cost}'}) / {'{Revenue}'} * 100</code></li>
              </ul>
            </div>
          </div>
        )}

        {/* Data Type */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Data Format</label>
          <select
            value={component.config.dataType}
            onChange={(e) => onUpdate({
              config: { ...component.config, dataType: e.target.value as any }
            })}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
          >
            <option value="number">Number</option>
            <option value="currency">Currency ($)</option>
            <option value="percentage">Percentage (%)</option>
            <option value="ratio">Ratio</option>
          </select>
        </div>

        {/* Unit */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Unit</label>
          <input
            type="text"
            value={component.config.unit || ''}
            onChange={(e) => onUpdate({
              config: { ...component.config, unit: e.target.value }
            })}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            placeholder="e.g., USD, customers, %, ratio"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Description</label>
          <textarea
            value={component.description || ''}
            onChange={(e) => onUpdate({ description: e.target.value })}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            rows={2}
            placeholder="Describe this metric..."
          />
        </div>

        {/* Metadata */}
        <div className="pt-3 border-t border-gray-200">
          <div className="text-xs font-semibold text-gray-700 mb-2">Metadata</div>
          <div className="space-y-1 text-xs text-gray-600">
            <div>ID: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{component.id}</code></div>
            {component.metadata?.tags && component.metadata.tags.length > 0 && (
              <div>Tags: <span className="font-medium">{component.metadata.tags.join(', ')}</span></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// FORMULA MODAL WITH AUTOCOMPLETE
// ============================================================================

// Excel functions for autocomplete
const EXCEL_FUNCTIONS = [
  { name: 'SUM', description: 'Adds all the numbers', example: '=SUM(A1, B1, C1)' },
  { name: 'AVERAGE', description: 'Calculates the average', example: '=AVERAGE(A1, B1)' },
  { name: 'MIN', description: 'Returns the minimum value', example: '=MIN(A1, B1, C1)' },
  { name: 'MAX', description: 'Returns the maximum value', example: '=MAX(A1, B1, C1)' },
  { name: 'IF', description: 'Conditional logic', example: '=IF(A1 > 0, B1, 0)' },
  { name: 'ABS', description: 'Absolute value', example: '=ABS(A1)' },
  { name: 'ROUND', description: 'Rounds to specified digits', example: '=ROUND(A1, 2)' },
  { name: 'SQRT', description: 'Square root', example: '=SQRT(A1)' },
  { name: 'POWER', description: 'Raises to power', example: '=POWER(A1, 2)' },
  { name: 'COUNT', description: 'Counts numbers', example: '=COUNT(A1, B1, C1)' },
  { name: 'COUNTA', description: 'Counts non-empty values', example: '=COUNTA(A1, B1)' },
  { name: 'CONCATENATE', description: 'Joins text', example: '=CONCATENATE(A1, " ", B1)' }
];

interface FormulaModalProps {
  connection: Connection;
  components: MetricComponent[];
  onSave: (formula: string, label?: string) => void;
  onClose: () => void;
}

export const FormulaModal: React.FC<FormulaModalProps> = ({
  connection,
  components,
  onSave,
  onClose
}) => {
  const [formula, setFormula] = useState(connection.formula || '');
  const [label, setLabel] = useState(connection.label || '');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteItems, setAutocompleteItems] = useState<Array<{ type: 'component' | 'function'; item: any }>>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const fromComp = components.find(c => c.id === connection.from);
  const toComp = components.find(c => c.id === connection.to);

  // Handle formula change with autocomplete
  const handleFormulaChange = (value: string) => {
    setFormula(value);

    if (textareaRef.current) {
      const pos = textareaRef.current.selectionStart;
      setCursorPosition(pos);

      // Get text before cursor
      const textBeforeCursor = value.substring(0, pos);
      const lastToken = textBeforeCursor.match(/[a-zA-Z_][a-zA-Z0-9_]*$/)?.[0] || '';

      if (lastToken.length > 0) {
        // Search for matching components
        const matchingComponents = components
          .filter(c => c.name.toLowerCase().includes(lastToken.toLowerCase()))
          .map(c => ({ type: 'component' as const, item: c }));

        // Search for matching functions
        const matchingFunctions = EXCEL_FUNCTIONS
          .filter(f => f.name.toLowerCase().startsWith(lastToken.toLowerCase()))
          .map(f => ({ type: 'function' as const, item: f }));

        const allMatches = [...matchingComponents, ...matchingFunctions];

        if (allMatches.length > 0) {
          setAutocompleteItems(allMatches);
          setShowAutocomplete(true);
          setSelectedIndex(0);
        } else {
          setShowAutocomplete(false);
        }
      } else {
        setShowAutocomplete(false);
      }
    }
  };

  // Handle autocomplete selection
  const handleAutocompleteSelect = (index: number) => {
    const selected = autocompleteItems[index];
    if (!selected || !textareaRef.current) return;

    const pos = cursorPosition;
    const textBefore = formula.substring(0, pos);
    const textAfter = formula.substring(pos);

    // Find the token to replace
    const lastTokenMatch = textBefore.match(/[a-zA-Z_][a-zA-Z0-9_]*$/);
    const tokenStart = lastTokenMatch ? pos - lastTokenMatch[0].length : pos;
    const beforeToken = formula.substring(0, tokenStart);

    let replacement = '';
    if (selected.type === 'component') {
      replacement = `{${selected.item.name}}`;
    } else {
      replacement = `${selected.item.name}()`;
    }

    const newFormula = beforeToken + replacement + textAfter;
    setFormula(newFormula);
    setShowAutocomplete(false);

    // Set cursor position after replacement
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = beforeToken.length + replacement.length - (selected.type === 'function' ? 1 : 0);
        textareaRef.current.selectionStart = newPos;
        textareaRef.current.selectionEnd = newPos;
        textareaRef.current.focus();
      }
    }, 0);
  };

  // Handle keyboard navigation in autocomplete
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showAutocomplete) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % autocompleteItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + autocompleteItems.length) % autocompleteItems.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleAutocompleteSelect(selectedIndex);
    } else if (e.key === 'Escape') {
      setShowAutocomplete(false);
    }
  };

  const handleSave = () => {
    if (formula.trim()) {
      onSave(formula.trim(), label.trim() || undefined);
    }
  };

  // Insert component reference at cursor
  const insertComponent = (comp: MetricComponent) => {
    if (!textareaRef.current) return;

    const pos = textareaRef.current.selectionStart;
    const before = formula.substring(0, pos);
    const after = formula.substring(pos);
    const newFormula = before + `{${comp.name}}` + after;

    setFormula(newFormula);

    // Set cursor after inserted text
    setTimeout(() => {
      if (textareaRef.current) {
        const newPos = before.length + comp.name.length + 2;
        textareaRef.current.selectionStart = newPos;
        textareaRef.current.selectionEnd = newPos;
        textareaRef.current.focus();
      }
    }, 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faCalculator} className="mr-2 text-purple-600" />
              Define Formula Connection
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
          {/* Connection Info */}
          <div className="flex items-center justify-center space-x-4 p-3 bg-gray-50 rounded">
            <div className="text-center">
              <div className="text-xs font-semibold text-gray-900">{fromComp?.name}</div>
              <div className="text-xs text-gray-500">{fromComp?.config.unit}</div>
            </div>
            <FontAwesomeIcon icon={faLink} className="text-purple-600" />
            <div className="text-center">
              <div className="text-xs font-semibold text-gray-900">{toComp?.name}</div>
              <div className="text-xs text-gray-500">{toComp?.config.unit}</div>
            </div>
          </div>

          {/* Short Label - optional override; otherwise auto-derived from formula */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Short Label <span className="text-gray-400 font-normal">(optional — auto-derived from formula if blank, e.g. "Revenue per Customer Count")</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={40}
              placeholder={autoDetectLabel(formula) || 'leave blank to auto-derive'}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
            />
          </div>

          {/* Formula Input with Autocomplete */}
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Excel-Style Formula (Type to see autocomplete)
            </label>
            <textarea
              ref={textareaRef}
              value={formula}
              onChange={(e) => handleFormulaChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm"
              rows={4}
              placeholder={`={${fromComp?.name}} / {${toComp?.name}}`}
              autoFocus
            />

            {/* Autocomplete Dropdown */}
            {showAutocomplete && autocompleteItems.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                {autocompleteItems.map((item, index) => (
                  <div
                    key={index}
                    className={`px-3 py-2 cursor-pointer flex items-start space-x-3 ${
                      index === selectedIndex ? 'bg-purple-50 border-l-2 border-purple-500' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleAutocompleteSelect(index)}
                  >
                    <div className={`flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                      item.type === 'component' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {item.type === 'component' ? 'C' : 'ƒ'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">
                        {item.type === 'component' ? item.item.name : item.item.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.type === 'component'
                          ? `${item.item.config.unit || item.item.config.dataType} • ${item.item.type.replace('_', ' ')}`
                          : item.item.description}
                      </div>
                      {item.type === 'function' && (
                        <div className="text-xs text-gray-400 font-mono mt-1">{item.item.example}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Insert - Components */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Quick Insert Components:
            </label>
            <div className="flex flex-wrap gap-2">
              {components.map((comp) => (
                <button
                  key={comp.id}
                  onClick={() => insertComponent(comp)}
                  className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                  title={comp.description}
                >
                  {comp.name}
                </button>
              ))}
            </div>
          </div>

          {/* Quick Insert - Excel Functions */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-2">
              Excel Functions:
            </label>
            <div className="grid grid-cols-3 gap-2">
              {EXCEL_FUNCTIONS.slice(0, 9).map((func) => (
                <button
                  key={func.name}
                  onClick={() => {
                    if (textareaRef.current) {
                      const pos = textareaRef.current.selectionStart;
                      const before = formula.substring(0, pos);
                      const after = formula.substring(pos);
                      const newFormula = before + `${func.name}()` + after;
                      setFormula(newFormula);
                      setTimeout(() => {
                        if (textareaRef.current) {
                          const newPos = before.length + func.name.length + 1;
                          textareaRef.current.selectionStart = newPos;
                          textareaRef.current.selectionEnd = newPos;
                          textareaRef.current.focus();
                        }
                      }, 0);
                    }
                  }}
                  className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 transition-colors"
                  title={func.description}
                >
                  {func.name}
                </button>
              ))}
            </div>
          </div>

          {/* Formula Examples */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-xs font-semibold text-blue-900 mb-2">💡 Formula Examples:</p>
            <div className="space-y-1 text-xs text-blue-800">
              <div>• Simple division: <code className="bg-white px-1 rounded">={`{${fromComp?.name}}`} / {`{${toComp?.name}}`}</code></div>
              <div>• With constant: <code className="bg-white px-1 rounded">={`{${fromComp?.name}}`} * 0.3</code></div>
              <div>• Sum: <code className="bg-white px-1 rounded">=SUM({`{${fromComp?.name}}`}, {`{${toComp?.name}}`})</code></div>
              <div>• Percentage: <code className="bg-white px-1 rounded">=({`{${fromComp?.name}}`} - {`{${toComp?.name}}`}) / {`{${fromComp?.name}}`} * 100</code></div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!formula.trim()}
            className="px-4 py-2 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Formula
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PREVIEW PANEL (unchanged from previous version)
// ============================================================================

interface PreviewPanelProps {
  visualizations: any[];
  components: MetricComponent[];
  executionResult: any;
  onClose: () => void;
}

export const PreviewPanel: React.FC<PreviewPanelProps> = ({
  visualizations,
  components,
  executionResult,
  onClose
}) => {
  return (
    <div className="h-64 bg-white border-t border-gray-300 overflow-y-auto">
      <div className="p-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-gray-900 flex items-center">
            <FontAwesomeIcon icon={faChartLine} className="mr-2 text-green-600" />
            Live Preview - Auto-Generated Visualizations
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {visualizations.map((viz, idx) => (
          <div key={idx} className="mb-4">
            <h4 className="text-xs font-semibold text-gray-800 mb-2">{viz.title}</h4>

            {viz.type === 'kpi' && (
              <div className="grid grid-cols-4 gap-2">
                {viz.componentIds.map((compId: string) => {
                  const comp = components.find(c => c.id === compId);
                  const result = executionResult?.results?.[compId];
                  return (
                    <div key={compId} className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded p-3">
                      <div className="text-xs text-gray-600 mb-1">{comp?.name}</div>
                      <div className="text-lg font-bold text-purple-700">
                        {result?.formatted || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">{comp?.config.unit}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {viz.type === 'bar' && (
              <div className="bg-gray-50 border border-gray-200 rounded p-4">
                <div className="flex items-end space-x-2 h-32">
                  {viz.componentIds.map((compId: string) => {
                    const comp = components.find(c => c.id === compId);
                    const result = executionResult?.results?.[compId];
                    const value = result?.value || 0;
                    const maxValue = Math.max(...viz.componentIds.map((id: string) =>
                      executionResult?.results?.[id]?.value || 0
                    ));
                    const height = (value / maxValue) * 100;

                    return (
                      <div key={compId} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-purple-500 rounded-t transition-all"
                          style={{ height: `${height}%` }}
                        />
                        <div className="text-xs text-gray-700 mt-1 text-center truncate w-full">
                          {comp?.name}
                        </div>
                        <div className="text-xs font-bold text-gray-900">{result?.formatted}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {viz.type === 'table' && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Metric</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Value</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Unit</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b">Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viz.componentIds.map((compId: string) => {
                      const comp = components.find(c => c.id === compId);
                      const result = executionResult?.results?.[compId];
                      return (
                        <tr key={compId} className="border-b hover:bg-gray-50">
                          <td className="px-3 py-2">{comp?.name}</td>
                          <td className="px-3 py-2 font-semibold">{result?.formatted || 'N/A'}</td>
                          <td className="px-3 py-2">{comp?.config.unit}</td>
                          <td className="px-3 py-2">
                            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                              {comp?.type?.replace('_', ' ')}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
